
<template>
  <div class="upload-attachment">
    <div class="upload-attachment__container">
      <span v-if="title" class="upload-attachment__container--header">{{ title }}</span>
      <div class="upload-attachment__container__file-upload">
        <file-upload
          ref="upload"
          v-model="filesToBeUploaded"
          :multiple="multiple"
          :drop="true"
          :drop-directory="true"
          name="file-upload"
          input-id="file-upload"
          @input-filter="inputFilter"
        >
          <slot name="upload-info">
            <icon class-name="upload-attachment__container__file-upload__icon" name="folder-arrow-up" size="24" />
            <span
              class="upload-attachment__container__file-upload__content-one"
            >Drag your files here or click here to upload</span>
            <span
              class="upload-attachment__container__file-upload__content-two"
            >{{ supportedFileFormatMessage }}</span>
            <span
              class="upload-attachment__container__file-upload__content-two"
            >Maximum {{ maximum }} {{ maximum > 1 ? 'files are' : 'file is' }} supported for each attempt</span>
            <span
              class="upload-attachment__container__file-upload__content-two"
            >Maximum supported size (Each File): {{ maxAttachmentSize/1000000 }} MB</span>
          </slot>
        </file-upload>
      </div>
    </div>
    <div v-if="filesToBeUploaded.length && isInstantUpload" class="upload-attachment__loading">
      <span class="upload-attachment__loading--header">Uploading</span>
      <div v-for="file in filesToBeUploaded" :key="file.id">
        <div class="upload-attachment__loading__file">
          <div class="upload-attachment__loading__file__content">
            <span class="upload-attachment__loading__file__content--name">{{ file.name }}</span>
            <span
              class="upload-attachment__loading__file__content--size"
            >Size: {{ getFileSize(file) }}</span>
            <loadingIndicator :loading="true" />
          </div>
          <attachment-item :file="file" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vUpload from "vue-upload-component";
// Components
import { showToast } from "../Toast";
import AttachmentItem from "./AttachmentItem.vue";
import Icon from "../Icon/Icon.vue";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator.vue";

export default {
  name: "UploadAttachment",
  components: {
    Icon,
    AttachmentItem,
    LoadingIndicator,
    "FileUpload": vUpload
  },
  props: {
      title: {
        type: String,
        default: 'Upload Attachments'
      },
      maximum: {
        type: Number,
        default: 1
      },
      multiple: {
        type: Boolean,
        default: false
      },
      maxAttachmentSize: {
        type: Number,
        default: 2000000,  // 2 MB in Bytes
      },
      isInstantUpload: {
        type: Boolean,
        default: false
      },
      supportedFileFormatMessage: {
        type: String,
        default: "Supported file formats: PNG, JPG, JPEG, PDF, XLS, CSV, XLSX and MSG",
      },
      supportedFileTypes: {
        type: Array,
        default: () => [
          "image/png", 
          "image/jpeg",
          "image/jpg",
          "image/webp",
          "application/pdf",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-outlook",
          "text/csv"
        ],
      }
  },
  data() {
    return {
      filesToBeUploaded: [],
      attachments: [],
    };
  },
  watch: {
    filesToBeUploaded() {
      if (this.filesToBeUploaded.length > this.maximum) {
        this.filesToBeUploaded.pop();
        showToast("error", "Maximum 3 files are supported");
      }
      this.filesToBeUploaded.forEach((item) => {
        const { type: fileType, size: fileSize, edit: isEdit } = item;
        const invalidFileTypePresent =
          this.supportedFileTypes.indexOf(fileType) > -1;
        if (!isEdit) {
          if (fileSize > this.maxAttachmentSize) {
            this.$refs.upload.remove(item);
            showToast("error", "Maximum Supported File Size - 2 MB");
          }
          if (!invalidFileTypePresent) {
            if (!(item.name.split(".").pop() === "msg")) {
              this.$refs.upload.remove(item);
              showToast(
                "error",
                `Invalid file type, ${this.supportedFileFormatMessage}`,
              );
            }
          }
        }
      });
      this.upload();
    },
  },
  methods: {
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Before adding a file
        // Filter system files or hide files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent();
        }
        // Filter php html js file
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent();
        }
      }
      if (
        newFile &&
        newFile.error === "" &&
        newFile.file &&
        (!oldFile || newFile.file !== oldFile.file)
      ) {
        // Create a blob field
        newFile.blob = "";
        let URL = window.URL || window.webkitURL;
        if (URL) {
          newFile.blob = URL.createObjectURL(newFile.file);
        }
        // Thumbnails
        newFile.thumb = "";
        if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
          newFile.thumb = newFile.blob;
        }
      }
      // image size
      if (
        newFile &&
        newFile.error === "" &&
        newFile.type.substr(0, 6) === "image/" &&
        newFile.blob &&
        (!oldFile || newFile.blob !== oldFile.blob)
      ) {
        newFile.error = "image parsing";
        let img = new Image();
        img.onload = () => {
          this.$refs.upload.update(newFile, {
            error: "",
            height: img.height,
            width: img.width,
          });
        };
        img.οnerrοr = () => {
          this.$refs.upload.update(newFile, { error: "parsing image size" });
        };
        img.src = newFile.blob;
      }
    },
    getFileSize(file) {
      const { size } = file;
      return size ? `${Math.round(size / 1000)} kb` : "";
    },
    upload() {
      this.$emit('uploadFiles', this.filesToBeUploaded, () => {
        if(this.isInstantUpload) {
          this.filesToBeUploaded = []
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-attachment {
  &__container {
    border-bottom: 1px solid var(--Gray-300);
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    &--header {
      font-family: "Maersk Text Bold";
      color: var(--Blue-900-Darkest);
    }

    &__file-upload {
      margin-top: 1rem;
      & .file-uploads {
        max-width: 40.75rem;
        height: 12rem;
        background: var(--Gray-200);
        border: 0.0625rem dashed var(--Gray-600);
        box-sizing: border-box;
        border-radius: 0.25rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      &__content-one {
        font-family: "Maersk Text Bold";
      }

      &__content-two {
        font-family: "Maersk Text";
        margin-top: 0.5rem;
        color: var(--Gray-700);
      }
    }
  }

  &__loading {
    width: 40.75rem;
    &--header {
      font-family: "Maersk Text Bold";
      color: var(--Blue-900-Darkest);
      display: block;
      margin-bottom: 1rem;
    }
    &__file {
      padding: 1rem;
      background: var(--red-variant-1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      &__content {
        display: flex;
        flex-direction: column;
        width: 61%;
        &--name {
          font-family: "Maersk Text Bold";
        }

        &--size {
          color: var(--Gray-700);
        }
        & .loading{
          margin-top: 16px;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.upload-attachment__loading {
  & .loading {
    margin: 0.5rem 0;
  }
  &__file {
    margin-top: 20px;
    &--icon {
      width: 14rem;
    }
  }
}
</style>